import { Component, OnInit, OnDestroy } from '@angular/core';
import { SystemService, SignalRService } from '../../services';
import { Subscription } from 'rxjs';

@Component({
  selector: 'wm-signalr-test',
  templateUrl: './signalr-test.component.html',
  styleUrl: './signalr-test.component.css',
  standalone: false
})
export class SignalrTestComponent implements OnInit, OnDestroy {
  applicationId = '';
  groupApplicationId = '';
  messageText = '';
  senderName = '';
  isConnected = false;
  isListening = false;
  listeningToApplicationId = '';
  connectionId = '';
  joinedGroups: string[] = [];
  messages: { timestamp: Date; applicationId: string; text: string }[] = [];
  connectionError: string | null = null;
  typingError: string | null = null;
  groupError: string | null = null;
  messageError: string | null = null;

  // Real messaging properties
  realMessageText = '';
  realMessageError: string | null = null;
  realMessageSuccess: string | null = null;
  applicationMessages: any[] = [];
  loadingMessages = false;
  messagesError: string | null = null;

  private typingSubscription: Subscription | null = null;
  private messageSubscription: Subscription | null = null;

  constructor(
    private systemService: SystemService,
    private signalRService: SignalRService
  ) { }

  ngOnInit(): void {
    // Subscribe to connection state changes
    this.signalRService.getConnectionState().subscribe(state => {
      this.isConnected = this.signalRService.isConnected;
    });

    // Auto-connect to hub on component load
    this.connectToHub();
  }

  async connectToHub(): Promise<void> {
    try {
      this.connectionError = null;

      // Connect to SignalR hub using hub name (not path)
      await this.signalRService.startConnection('MessagingHub', () => {
        const token = sessionStorage.getItem('id_token') || '';
        console.log('Using access token:', token ? 'Token present' : 'No token');
        return token;
      });

      // Register the server-to-client method
      this.signalRService.on('typingNotification', (data: any) => {
        console.log('Received typing notification:', data);
        this.addMessage(
          data.ApplicationId || 'Unknown',
          `User is typing - Timestamp: ${data.Timestamp}`
        );
      });

      // Register the new message received handler
      this.signalRService.on('newMessageReceived', (data: any) => {
        console.log('Received new message notification:', data);
        this.addMessage(
          data.ApplicationId || 'Unknown',
          `New message from ${data.SenderName}: "${data.MessageText}" (ID: ${data.MessageId})`
        );
      });

      this.isConnected = true;

      // Get connection ID (if available)
      this.getConnectionId();

      this.addMessage('System', 'Connected to SignalR hub');
    } catch (error: any) {
      this.connectionError = error.message || 'Failed to connect to SignalR hub';
      console.error('Connection error:', error);
    }
  }
  // async connectToHub(): Promise<void> {
  //   try {
  //     this.connectionError = null;

  //     // Provide access token factory
  //     await this.signalRService.startConnection('/api/messagingHub', () => {
  //       return sessionStorage.getItem('id_token') || '';
  //     });

  //     this.isConnected = true;
  //     this.addMessage('System', 'Connected to SignalR hub');
  //   } catch (error: any) {
  //     this.connectionError = error.message || 'Failed to connect to SignalR hub';
  //     console.error('Connection error:', error);
  //     console.error('Full error:', error);
  //   }
  // }

  async disconnectFromHub(): Promise<void> {
    try {
      this.stopListening();
      await this.signalRService.stopConnection();
      this.isConnected = false;
      this.connectionId = '';
      this.joinedGroups = [];
      this.addMessage('System', 'Disconnected from SignalR hub');
    } catch (error) {
      console.error('Disconnect error:', error);
    }
  }

  async startListening(): Promise<void> {
    if (!this.isConnected) {
      this.connectionError = 'Must connect to hub first';
      return;
    }

    if (!this.applicationId) {
      this.connectionError = 'Application ID is required to start listening';
      return;
    }

    // Join the application group first
    try {
      await this.signalRService.invoke('JoinApplicationGroup', this.applicationId);

      if (!this.joinedGroups.includes(this.applicationId)) {
        this.joinedGroups.push(this.applicationId);
      }

      this.listeningToApplicationId = this.applicationId;
      this.addMessage(
        this.applicationId,
        `Joined and listening to application group: app_${this.applicationId}`
      );
    } catch (error: any) {
      this.connectionError = 'Failed to join application group: ' + (error.message || error);
      console.error('Join group error:', error);
      return;
    }

    this.systemService.listenForMessages(this.applicationId);

    // Subscribe to typing messages - filter by application
    this.typingSubscription = this.systemService.typingReceived$.subscribe(
      (message: any) => {
        const appId = message.ApplicationId || message.applicationId;
        // Only show typing notifications for the application we're listening to
        if (appId === this.listeningToApplicationId) {
          this.addMessage(
            appId,
            `User is typing...`
          );
        }
      }
    );

    // Subscribe to new message notifications - filter by application
    this.messageSubscription = this.systemService.newMessageReceived$.subscribe(
      (message: any) => {
        const appId = message.ApplicationId;
        // Only show messages for the application we're listening to
        if (appId === this.listeningToApplicationId) {
          this.addMessage(
            appId,
            `New message from ${message.SenderName}: "${message.MessageText}"`
          );

          // Auto-refresh messages if we have some loaded
          if (this.applicationMessages.length > 0 && this.applicationId === appId) {
            this.loadApplicationMessages();
          }
        }
      }
    );

    this.isListening = true;
    this.addMessage('System', `Started listening for messages on application: ${this.listeningToApplicationId}`);
  }

  async stopListening(): Promise<void> {
    if (this.typingSubscription) {
      this.typingSubscription.unsubscribe();
      this.typingSubscription = null;
    }

    if (this.messageSubscription) {
      this.messageSubscription.unsubscribe();
      this.messageSubscription = null;
    }

    // Leave the application group if we were listening to one
    if (this.listeningToApplicationId && this.isConnected) {
      try {
        await this.signalRService.invoke('LeaveApplicationGroup', this.listeningToApplicationId);

        const index = this.joinedGroups.indexOf(this.listeningToApplicationId);
        if (index > -1) {
          this.joinedGroups.splice(index, 1);
        }

        this.addMessage(
          this.listeningToApplicationId,
          `Left application group: app_${this.listeningToApplicationId}`
        );
      } catch (error) {
        console.error('Error leaving group:', error);
      }
    }

    await this.systemService.stopListeningForMessages(this.applicationId);
    this.isListening = false;
    this.listeningToApplicationId = '';
    this.addMessage('System', 'Stopped listening for messages');
  }

  sendTypingNotification(): void {
    if (!this.isConnected) {
      this.typingError = 'Must connect to hub first';
      return;
    }

    if (!this.applicationId) {
      this.typingError = 'Application ID is required';
      return;
    }

    this.typingError = null;

    // Use system service method which calls SendTypingNotification
    this.systemService.typingMessage(this.applicationId);

    this.addMessage(
      this.applicationId,
      `Sent typing notification to group app_${this.applicationId}`
    );
  }

  sendTypingNotificationToAll(): void {
    if (!this.isConnected) {
      this.typingError = 'Must connect to hub first';
      return;
    }

    if (!this.applicationId) {
      this.typingError = 'Application ID is required';
      return;
    }

    this.typingError = null;

    // Call the hub method - sends to ALL clients
    this.signalRService.invoke('SendTypingNotificationToAll', this.applicationId)
      .then(() => {
        this.addMessage(
          this.applicationId,
          `Sent typing notification to ALL clients for application: ${this.applicationId}`
        );
      })
      .catch((error: any) => {
        this.typingError = error.message || 'Failed to send typing notification';
        console.error('Typing notification error:', error);
      });
  }

  async joinGroup(): Promise<void> {
    if (!this.isConnected) {
      this.groupError = 'Must connect to hub first';
      return;
    }

    if (!this.groupApplicationId) {
      this.groupError = 'Application ID is required';
      return;
    }

    this.groupError = null;

    try {
      await this.signalRService.invoke('JoinApplicationGroup', this.groupApplicationId);

      if (!this.joinedGroups.includes(this.groupApplicationId)) {
        this.joinedGroups.push(this.groupApplicationId);
      }

      this.addMessage(
        this.groupApplicationId,
        `Joined application group: app_${this.groupApplicationId}`
      );
    } catch (error: any) {
      this.groupError = error.message || 'Failed to join group';
      console.error('Join group error:', error);
    }
  }

  async leaveGroup(): Promise<void> {
    if (!this.isConnected) {
      this.groupError = 'Must connect to hub first';
      return;
    }

    if (!this.groupApplicationId) {
      this.groupError = 'Application ID is required';
      return;
    }

    this.groupError = null;

    try {
      await this.signalRService.invoke('LeaveApplicationGroup', this.groupApplicationId);

      const index = this.joinedGroups.indexOf(this.groupApplicationId);
      if (index > -1) {
        this.joinedGroups.splice(index, 1);
      }

      this.addMessage(
        this.groupApplicationId,
        `Left application group: app_${this.groupApplicationId}`
      );
    } catch (error: any) {
      this.groupError = error.message || 'Failed to leave group';
      console.error('Leave group error:', error);
    }
  }

  removeFromJoinedGroups(groupId: string): void {
    this.leaveGroupById(groupId);
  }

  async leaveGroupById(groupId: string): Promise<void> {
    this.groupApplicationId = groupId;
    await this.leaveGroup();
  }

  sendNewMessageNotification(): void {
    if (!this.isConnected) {
      this.messageError = 'Must connect to hub first';
      return;
    }

    if (!this.applicationId || !this.messageText || !this.senderName) {
      this.messageError = 'Application ID, message text, and sender name are required';
      return;
    }

    this.messageError = null;

    // Generate a mock message ID
    const messageId = `msg_${Date.now()}`;

    try {
      this.systemService.notifyNewMessage(this.applicationId, messageId, this.messageText, this.senderName);
      this.addMessage(
        this.applicationId,
        `Sent new message notification to group app_${this.applicationId}`
      );
      // Clear the message text after sending
      this.messageText = '';
    } catch (error: any) {
      this.messageError = error.message || 'Failed to send new message notification';
      console.error('New message notification error:', error);
    }
  }

  // Send a real message using system service (saves to database and notifies group)
  sendRealMessage(): void {
    if (!this.applicationId || !this.realMessageText) {
      this.realMessageError = 'Application ID and message text are required';
      return;
    }

    this.realMessageError = null;
    this.realMessageSuccess = null;

    this.systemService.sendApplicationMessage(this.applicationId, this.realMessageText)
      .subscribe({
        next: (response) => {
          this.realMessageSuccess = `Message sent successfully! ID: ${response.id}`;
          this.addMessage(
            this.applicationId,
            `Sent real message: "${this.realMessageText}" (ID: ${response.id})`
          );
          // Clear the message text after sending
          this.realMessageText = '';

          // Auto-refresh messages if we're viewing this application
          setTimeout(() => this.realMessageSuccess = null, 3000);
        },
        error: (error: any) => {
          this.realMessageError = error.message || 'Failed to send message';
          console.error('Send message error:', error);
        }
      });
  }

  // Get all messages for the application
  loadApplicationMessages(): void {
    if (!this.applicationId) {
      this.messagesError = 'Application ID is required';
      return;
    }

    this.loadingMessages = true;
    this.messagesError = null;

    this.systemService.getWorkflowApplicationMessages(this.applicationId)
      .subscribe({
        next: (response) => {
          this.applicationMessages = response.messages || [];
          this.loadingMessages = false;
          this.addMessage(
            this.applicationId,
            `Loaded ${this.applicationMessages.length} messages for application`
          );
        },
        error: (error: any) => {
          this.messagesError = error.message || 'Failed to load messages';
          this.loadingMessages = false;
          console.error('Load messages error:', error);
        }
      });
  }

  // Mark a message as read
  markMessageAsRead(messageId: string): void {
    this.systemService.markAsRead(messageId)
      .subscribe({
        next: (response) => {
          this.addMessage(
            this.applicationId,
            `Marked message ${messageId} as read`
          );

          // Update the message in the list
          const message = this.applicationMessages.find(m => m.id === messageId);
          if (message) {
            message.hasRead = true;
          }
        },
        error: (error: any) => {
          console.error('Mark as read error:', error);
          this.addMessage(
            'Error',
            `Failed to mark message ${messageId} as read: ${error.message}`
          );
        }
      });
  }

  private getConnectionId(): void {
    // Access the underlying connection to get the ID
    try {
      const connection = (this.signalRService as any).hubConnection?.connection;
      if (connection && connection.id) {
        this.connectionId = connection.id;
        this.addMessage('System', `Connection ID: ${this.connectionId}`);
      }
    } catch (error) {
      console.error('Error getting connection ID:', error);
    }
  }
  // sendTypingNotification(): void {
  //   if (!this.isConnected) {
  //     this.typingError = 'Must connect to hub first';
  //     return;
  //   }

  //   if (!this.applicationId) {
  //     this.typingError = 'Application ID is required';
  //     return;
  //   }

  //   this.typingError = null;
  //   this.systemService.typingMessage(this.applicationId).subscribe({
  //     next: () => {
  //       this.addMessage(
  //         this.applicationId,
  //         `Sent typing notification for application: ${this.applicationId}`
  //       );
  //     },
  //     error: (error: any) => {
  //       this.typingError = error.message || 'Failed to send typing notification';
  //       console.error('Typing notification error:', error);
  //     }
  //   });
  // }

  clearMessages(): void {
    this.messages = [];
  }

  private addMessage(applicationId: string, text: string): void {
    this.messages.unshift({
      timestamp: new Date(),
      applicationId,
      text
    });

    // Keep only last 50 messages
    if (this.messages.length > 50) {
      this.messages = this.messages.slice(0, 50);
    }
  }

  ngOnDestroy(): void {
    this.stopListening();
    if (this.typingSubscription) {
      this.typingSubscription.unsubscribe();
    }
    if (this.messageSubscription) {
      this.messageSubscription.unsubscribe();
    }
  }
}
